import { useParams, usePathname } from 'next/navigation'

import { getDiscountProductIdFromElementPath, useDiscountProductQuery } from '@/entities/catalog-discount'
import { DiscountProductConditionSpecs } from '@/entities/catalog-discount/schema/discount-product-detail.schema'
import { useCrossLinksQuery, useProductQuery } from '@/entities/product'

import { PRODUCT_ID_URL_PREFIX } from '@/shared/lib/utils/constants/const'
import { TProduct } from '@/shared/types/product-types'

export type TCrossLinksGroup = {
	name: string
	type: string
	values: TCrossLink[]
}

export type TCrossLink = {
	kod: string
	value: string
	label: string
	is_current: boolean
	hash: string
	in_stock: string
}

export type ProductContextValue = {
	product: TProduct | null
	crossLinks?: TCrossLinksGroup[]
	isCrossLinksLoading?: boolean
	conditionSpecs?: DiscountProductConditionSpecs
	discountKod?: string
}

export const useIsDiscountProductPage = (): boolean => {
	const pathname = usePathname()

	return pathname?.includes('/discount/products/') ?? false
}

export const useProduct = (): ProductContextValue => {
	const params = useParams()
	const elementPath = params?.elementPath as string
	const isDiscountProductPage = useIsDiscountProductPage()
	const productCode = elementPath?.replace(PRODUCT_ID_URL_PREFIX, ``) ?? ``
	const productId = isDiscountProductPage
		? getDiscountProductIdFromElementPath(decodeURIComponent(elementPath ?? ``))
		: ``

	const { data: discountPageData = null } = useDiscountProductQuery(productId)
	const { data: product = null } = useProductQuery(isDiscountProductPage ? `` : productCode)
	const { data: crossLinks = [], isLoading: isCrossLinksLoading } = useCrossLinksQuery(
		isDiscountProductPage ? `` : productCode
	)

	if (isDiscountProductPage) {
		return {
			product: discountPageData?.product ?? null,
			crossLinks: [],
			isCrossLinksLoading: false,
			conditionSpecs: discountPageData?.conditionSpecs,
			discountKod: discountPageData?.discountKod
		}
	}

	return {
		product: product as TProduct | null,
		crossLinks: crossLinks as TCrossLinksGroup[],
		isCrossLinksLoading
	}
}
